import {
  PyCommentContainer,
  PyCommentHeader,
  PyCommentAuthor,
  PyCommentTimestamp,
  PyCommentContent,
  PyCommentText,
  PyCommentMention,
  PyPrivateLabel,
  PyPublicLabel,
  PyCommentTimestampText,
  PyCommentHeaderLeftSection,
  PyCommentMenuIconButton,
  PyCommentMenuIcon,
  PyCommentTimeIcon,
  PyCommentDeleteOverlay,
  PyCommentDeleteText,
  PyCommentDeleteActionsContainer,
  PyCommentDeleteSubmitButton,
} from './styled';

import type { CommentBody, CommentData } from '@liveblocks/client';
import Button from '@mui/material/Button';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { formatTimestamp } from '../../helpers/formatTimestamp';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useComments } from 'providers/CommentsContext';
import { CustomComposer } from './CustomComposer';
import { RoomType } from 'providers/RoomRegistrar';
import type { ComposerSubmitComment } from '@liveblocks/react-ui/primitives';
import { useUser } from '@liveblocks/react';

interface CommentProps {
  comment: CommentData;
  mainComment?: boolean;
  onEditComment: (commentId: string, body: CommentBody) => void;
  onDeleteComment?: (commentId: string) => void;
  roomType: RoomType;
}

export const Comment = ({ comment, mainComment = false, onEditComment, onDeleteComment, roomType }: CommentProps) => {
  const { t } = useTranslation();
  const { canEditComment, canDeleteComment } = useComments();

  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const { user, isLoading: isUserLoading } = useUser(comment.userId);
  const commentAuthor = user?.name || (isUserLoading ? '' : t('comments.anonymous'));

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEdit = async (updatedComment: ComposerSubmitComment) => {
    onEditComment(comment.id, updatedComment.body);
    setIsEditing(false);
  };

  const handleDelete = () => {
    onDeleteComment?.(comment.id);
    setIsDeleting(false);
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    handleMenuClose();
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  return (
    <PyCommentContainer>
      <PyCommentHeader>
        <PyCommentHeaderLeftSection>
          {commentAuthor ? (
            <Tooltip title={commentAuthor} arrow placement="top">
              <PyCommentAuthor>{commentAuthor}</PyCommentAuthor>
            </Tooltip>
          ) : (
            <PyCommentAuthor>{commentAuthor}</PyCommentAuthor>
          )}
          {mainComment &&
            (roomType === RoomType.PRIVATE ? (
              <PyPrivateLabel>{t('comments.private')}</PyPrivateLabel>
            ) : (
              <PyPublicLabel>{t('comments.public')}</PyPublicLabel>
            ))}
        </PyCommentHeaderLeftSection>
        {(canEditComment(comment) || canDeleteComment(comment)) && (
          <>
            <PyCommentMenuIconButton
              size="small"
              onClick={handleMenuOpen}
              aria-controls={menuAnchorEl ? 'thread-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={menuAnchorEl ? 'true' : undefined}
            >
              <PyCommentMenuIcon />
            </PyCommentMenuIconButton>

            <Menu
              id="thread-menu"
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
              MenuListProps={{
                'aria-labelledby': 'thread-menu-button',
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {canEditComment(comment) && (
                <MenuItem onClick={handleStartEdit}>
                  <ListItemText>{t('comments.edit')}</ListItemText>
                </MenuItem>
              )}
              {!mainComment && canDeleteComment(comment) && (
                <MenuItem
                  onClick={() => {
                    setIsDeleting(true);
                    handleMenuClose();
                  }}
                >
                  <ListItemText>{t('comments.delete')}</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </PyCommentHeader>
      <PyCommentTimestamp>
        <PyCommentTimeIcon />
        <PyCommentTimestampText>{formatTimestamp(comment.createdAt)}</PyCommentTimestampText>
      </PyCommentTimestamp>

      <PyCommentContent>
        {isEditing ? (
          <CustomComposer
            roomType={roomType}
            onCancel={handleCancelEdit}
            onSubmit={handleEdit}
            placeholder={t('comments.enterComment')}
            submitLabel={t('comments.edit')}
            cancelLabel={t('comments.cancel')}
            defaultValue={comment.body}
          />
        ) : (
          <PyCommentText body={comment.body} components={{ Mention: PyCommentMention }} />
        )}
      </PyCommentContent>

      {isDeleting && (
        <PyCommentDeleteOverlay>
          <PyCommentDeleteText>{t('comments.deleteText')}</PyCommentDeleteText>
          <PyCommentDeleteActionsContainer>
            <Button type="button" onClick={() => setIsDeleting(false)} size="small">
              {t('comments.cancel')}
            </Button>
            <PyCommentDeleteSubmitButton type="submit" onClick={handleDelete} size="small">
              {t('comments.deleteComment')}
            </PyCommentDeleteSubmitButton>
          </PyCommentDeleteActionsContainer>
        </PyCommentDeleteOverlay>
      )}
    </PyCommentContainer>
  );
};
